"use client";

import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Info, Zap, Wind, Thermometer, Activity, Cpu, Settings, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const products = [{
  id: "chillers",
  label: "Chillers",
  title: "Chillers de Alta Precisão",
  description: "Projetados para máxima eficiência energética em processos industriais e hospitalares. Nossos chillers utilizam compressores de última geração e fluídos refrigerantes ecológicos, garantindo estabilidade térmica mesmo em cargas variáveis.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553755/img/doctorice_chiller_industrial.png",
  hotspots: [{
    id: "c1",
    x: "30%",
    y: "40%",
    icon: <Zap className="w-4 h-4" />,
    title: "Compressor Inverter",
    detail: "Tecnologia de velocidade variável que ajusta a capacidade conforme a demanda, reduzindo o consumo energético em até 30%."
  }, {
    id: "c2",
    x: "65%",
    y: "25%",
    icon: <Thermometer className="w-4 h-4" />,
    title: "Condensador Microchannel",
    detail: "Trocadores de calor em alumínio que maximizam a eficiência térmica e reduzem a carga de refrigerante necessária."
  }, {
    id: "c3",
    x: "50%",
    y: "75%",
    icon: <Cpu className="w-4 h-4" />,
    title: "Controle PLC Avançado",
    detail: "Interface inteligente com protocolos de comunicação (Modbus/Bacnet) para integração total ao BMS do edifício."
  }]
}, {
  id: "self-contained",
  label: "Self-Contained",
  title: "Self-Contained para Data Centers",
  description: "Soluções compactas e robustas para climatização de precisão. Ideais para ambientes que exigem controle rigoroso de temperatura e umidade, como data centers e salas de telecomunicações.",
  image: "https://doctorice.10web.cloud/wp-content/uploads/2026/03/9ab40fa1-ffc5-496f-8550-168424fe9723.png?v=x35p837c3ph",
  hotspots: [{
    id: "s1",
    x: "50%",
    y: "20%",
    icon: <Wind className="w-4 h-4" />,
    title: "Ventiladores EC",
    detail: "Ventiladores eletronicamente comutados (EC) para fluxo de ar preciso e operação silenciosa."
  }, {
    id: "s2",
    x: "25%",
    y: "60%",
    icon: <Activity className="w-4 h-4" />,
    title: "Controle de Umidade",
    detail: "Sistema integrado de umidificação e desumidificação para manter os níveis ideais para equipamentos eletrônicos sensíveis."
  }]
}, {
  id: "fancoils",
  label: "Fan-Coils",
  title: "Fan-Coils Hospitalares",
  description: "Unidades de tratamento de ar desenvolvidas especificamente para ambientes críticos. Garantem a qualidade do ar interior com filtragem de alto desempenho e facilidade de higienização.",
  image: "https://doctorice.10web.cloud/wp-content/uploads/2026/03/WhatsApp-Image-2026-03-11-at-18.42.19.jpeg?v=4jr0ozf0vje",
  hotspots: [{
    id: "f1",
    x: "40%",
    y: "50%",
    icon: <Settings className="w-4 h-4" />,
    title: "Filtragem HEPA",
    detail: "Estágios de filtragem configuráveis (G4 + F9 + HEPA) para retenção de partículas e contaminantes biológicos."
  }, {
    id: "f2",
    x: "70%",
    y: "35%",
    icon: <Info className="w-4 h-4" />,
    title: "Gabinete Sanitário",
    detail: "Construção com cantos arredondados e isolamento térmico que evita acúmulo de sujeira e proliferação de fungos."
  }, {
    id: "f3",
    x: "20%",
    y: "80%",
    icon: <Zap className="w-4 h-4" />,
    title: "Válvula de Balanceamento",
    detail: "Controle hidrônico preciso para garantir a vazão correta de água gelada em todas as condições de operação."
  }]
}];
export default function InovacaoEmEquipamentos() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [activeTab, setActiveTab] = useState("chillers");
  return <section id="inovação-em-equipamentos" ref={ref} className={`relative py-24 overflow-hidden bg-background transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <Tabs defaultValue="chillers" value={activeTab} onValueChange={setActiveTab} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className="relative order-2 lg:order-1 w-full aspect-square lg:aspect-[4/3] bg-muted/30 rounded-2xl border border-border overflow-hidden shadow-2xl group">
            {products.map((product, i) => <TabsContent key={product.id} value={product.id} className="w-full h-full relative animate-in fade-in zoom-in-95 duration-500 data-[state=inactive]:hidden" data-index={i}>
                <img src={product.image} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                
                {}
                <div className="absolute inset-0 bg-black/10 pointer-events-none" />

                {}
                {product.hotspots.map((hotspot, j) => <div key={hotspot.id} className="absolute z-20" style={{
              top: hotspot.y,
              left: hotspot.x
            }} data-index={j}>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="secondary" size="icon" className="relative w-8 h-8 rounded-full bg-white text-primary shadow-lg hover:scale-110 transition-transform hover:bg-white/90 p-0" aria-label={`Ver detalhes de ${hotspot.title}`}>
                          <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping pointer-events-none"></span>
                          <span className="relative z-10 flex items-center justify-center">
                            {hotspot.icon}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent side="top" className="w-72 p-5 bg-popover/95 backdrop-blur-sm border-border shadow-xl">
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-foreground flex items-center gap-2 border-b border-border pb-2">
                            <span className="text-primary">{hotspot.icon}</span>
                            {hotspot.title}
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {hotspot.detail}
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>)}
              </TabsContent>)}
          </div>

          {}
          <div className="order-1 lg:order-2 flex flex-col justify-center space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent-foreground text-xs font-medium w-fit border border-accent/30">
                <Settings className="w-3 h-3" />
                Engenharia de Ponta
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Inovação em Equipamentos
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Desenvolvemos e fabricamos equipamentos com tecnologia de ponta,
                focados em confiabilidade operacional e eficiência energética
                para as aplicações mais críticas do mercado.
              </p>
            </div>

            <div className="space-y-8">
              <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-transparent p-0 h-auto">
                {products.map((product, i) => <TabsTrigger key={product.id} value={product.id} data-index={i} className="rounded-full border border-border bg-background px-6 py-2.5 text-sm font-medium text-muted-foreground shadow-sm transition-all hover:bg-accent hover:text-accent-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md cursor-pointer">
                    {product.label}
                  </TabsTrigger>)}
              </TabsList>

              <div className="min-h-[200px]">
                {products.map((product, i) => <TabsContent key={product.id} value={product.id} className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-500 data-[state=inactive]:hidden" data-index={i}>
                    <div className="space-y-4 border-l-2 border-primary pl-6">
                      <h3 className="text-2xl font-semibold text-foreground">
                        {product.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {product.description}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 pt-4">
                      <Button asChild size="lg" className="shadow-md">
                        <Link to="/EquipmentPage">
                          Ver Especificações
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                      <Button variant="outline" size="lg" asChild className="bg-background hover:bg-accent">
                        <Link to="/ContactPage">
                          Solicitar Orçamento
                        </Link>
                      </Button>
                    </div>
                  </TabsContent>)}
              </div>
            </div>
          </div>
        </Tabs>
      </div>
    </section>;
}